import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchActiveCountries } from '@/shared/api/places';
import { toOptions } from '@/shared/lib/options';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { toSearchParams } from '@/shared/lib/search';
import { useListFilters } from '@/shared/lib/useListFilters';
import { FilterActions } from '@/shared/ui/FilterActions';
import { FilterField } from '@/shared/ui/FilterField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { SearchKeyInput } from '@/shared/ui/SearchKeyInput';
import { SortableTh } from '@/shared/ui/SortableTh';
import { fetchRegionsList, type Region } from './api';
import { RegionModal } from './RegionModal';

const SEARCH_KEY_OPTIONS = [
  { value: 'id', label: 'Поиск по id' },
  { value: 'name', label: 'Поиск по региону' },
];

const emptyFilters = { searchKey: '', searchValue: '', countryId: '' };

/** `null` — closed, `{}` — create, `{ id }` — edit. */
type ModalState = { id?: number } | null;

export const RegionsPage = () => {
  const list = useListFilters(emptyFilters);
  const { draft, applied, page, sortField, sortOrder } = list;
  const [modal, setModal] = useState<ModalState>(null);

  const countries = useQuery({ queryKey: ['countries-active'], queryFn: fetchActiveCountries });

  const params = {
    page,
    sort_field: sortField,
    sort_order: sortOrder,
    ...toSearchParams(applied.searchKey, applied.searchValue),
    country_id: applied.countryId,
  };
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['place-regions', params],
    queryFn: () => fetchRegionsList(params),
  });
  const rows = getListRows<Region>(data, ['regions']);

  return (
    <>
      <section className="content-header">
        <h1>Справочник мест</h1>
        <div className="row">
          <div className="col-sm-12">
            <button type="button" className="btn btn-info pull-right" onClick={() => setModal({})}>
              Создать
            </button>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Регион</h3>
            <form onSubmit={list.submit} style={{ marginTop: 12 }}>
              <FilterField label="Поиск">
                <SearchKeyInput
                  keyOptions={SEARCH_KEY_OPTIONS}
                  searchKey={draft.searchKey}
                  searchValue={draft.searchValue}
                  onKeyChange={(v) => list.setField('searchKey', v)}
                  onValueChange={(v) => list.setField('searchValue', v)}
                />
              </FilterField>
              <FilterField label="Фильтрация по стране">
                <FilterSelect
                  options={toOptions(countries.data)}
                  value={draft.countryId}
                  isLoading={countries.isLoading}
                  onChange={(v) => list.setField('countryId', v)}
                />
              </FilterField>
              <FilterActions disabled={isFetching} onReset={list.reset} />
            </form>
          </div>
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки регионов" />
            <table className="table table-bordered">
              <thead>
                <tr>
                  <SortableTh active={sortField === 'id'} onClick={() => list.toggleSort('id')}>
                    id
                  </SortableTh>
                  <SortableTh active={sortField === 'name'} onClick={() => list.toggleSort('name')}>
                    Регион
                  </SortableTh>
                  <SortableTh
                    active={sortField === 'country_id'}
                    onClick={() => list.toggleSort('country_id')}
                  >
                    Страна
                  </SortableTh>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((region) => (
                  <tr key={region.id}>
                    <td>{region.id}</td>
                    <td>{region.name}</td>
                    <td>{region.country ? region.country.name || 'No country.name' : 'No country'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => setModal({ id: region.id })}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={getTotalPages(data)} onChange={list.setPage} />
          </div>
        </div>
      </section>
      {modal && <RegionModal key={modal.id ?? 'new'} id={modal.id} onClose={() => setModal(null)} />}
    </>
  );
};
